import { ChevronLeft, ChevronRight } from 'lucide-react'

import { cn } from '@/lib/cn'

interface PaginationProps {
  page: number
  size: number
  total: number
  onPageChange: (page: number) => void
}

export function Pagination({ page, size, total, onPageChange }: PaginationProps) {
  const pageCount = Math.max(1, Math.ceil(total / size))
  if (total === 0) return null

  const from = (page - 1) * size + 1
  const to = Math.min(page * size, total)
  const pages = visiblePages(page, pageCount)

  return (
    <nav
      className="flex flex-wrap items-center justify-between gap-4 border-t border-outline/10 px-6 py-4"
      aria-label="Paginacja"
    >
      <p className="text-sm text-on-surface-variant">
        Wyświetlono <strong className="font-semibold text-on-surface">{from}</strong>–
        <strong className="font-semibold text-on-surface">{to}</strong> z{' '}
        <strong className="font-semibold text-on-surface">{total}</strong>
      </p>

      <div className="flex items-center gap-1">
        <PageButton
          label="Poprzednia strona"
          disabled={page <= 1}
          onClick={() => onPageChange(page - 1)}
        >
          <ChevronLeft size={16} />
        </PageButton>

        {pages.map((item, index) =>
          item === '…' ? (
            <span key={`gap-${index}`} className="px-2 text-sm text-on-surface-variant">
              …
            </span>
          ) : (
            <button
              key={item}
              type="button"
              aria-current={item === page ? 'page' : undefined}
              onClick={() => onPageChange(item)}
              className={cn(
                'min-w-9 rounded-lg px-3 py-1.5 text-sm font-bold transition-colors',
                item === page
                  ? 'bg-primary text-on-primary'
                  : 'text-on-surface-variant hover:bg-surface-container-low',
              )}
            >
              {item}
            </button>
          ),
        )}

        <PageButton
          label="Następna strona"
          disabled={page >= pageCount}
          onClick={() => onPageChange(page + 1)}
        >
          <ChevronRight size={16} />
        </PageButton>
      </div>
    </nav>
  )
}

function PageButton({
  label,
  disabled,
  onClick,
  children,
}: {
  label: string
  disabled: boolean
  onClick: () => void
  children: React.ReactNode
}) {
  return (
    <button
      type="button"
      aria-label={label}
      disabled={disabled}
      onClick={onClick}
      className="rounded-lg p-2 text-on-surface-variant transition-colors hover:bg-surface-container-low disabled:cursor-not-allowed disabled:opacity-30"
    >
      {children}
    </button>
  )
}

/** Zwraca numery stron z wielokropkami, np. 1 … 4 5 6 … 20. */
function visiblePages(current: number, pageCount: number): (number | '…')[] {
  if (pageCount <= 7) {
    return Array.from({ length: pageCount }, (_, index) => index + 1)
  }

  const pages = new Set<number>([1, pageCount, current, current - 1, current + 1])
  const sorted = [...pages].filter((page) => page >= 1 && page <= pageCount).sort((a, b) => a - b)

  const result: (number | '…')[] = []
  let previous = 0
  for (const page of sorted) {
    if (previous && page - previous > 1) result.push('…')
    result.push(page)
    previous = page
  }
  return result
}
